import Assets from "features/portfolio/Assets/Assets";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  PORTFOLIO_ACCOUNTS_PATH,
  PORTFOLIO_ASSETS_PATH,
  PORTFOLIO_POSITIONS_PATH,
  PORTFOLIO_TABS_PATH,
} from "common/constants";
import { FilterType, LanguageNameSpaces, PortfolioTab } from "@shared/types";
import Positions from "features/portfolio/Positions/Positions.tsx";
import Tabs from "common/Tabs/Tabs.tsx";
import useTabsWithUrl from "common/Tabs/useTabsWithUrl.tsx";
import Flex from "common/Boxes/Flex.tsx";
import StickyHeader from "common/Headers/StickyHeader.tsx";
import styled, { useTheme } from "styled-components";
import useSearchInput from "utils/hooks/useSearchInput.tsx";
import { PositionsSort } from "@shared/features/portfolio/types.ts";
import { PortfolioContext } from "features/portfolio/Portfolio.context.ts";
import LayoutSwitch from "common/Switch/LayoutSwitch.tsx";
import MenuOutlined from "@ant-design/icons/MenuOutlined";
import TableOutlined from "@ant-design/icons/TableOutlined";
import SortSelect from "common/Select/SortSelect.tsx";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import Button from "common/Buttons/Button.tsx";
import {
  selectConsolidatedPortfolioData,
  selectPortfolioDataLoadingStates,
} from "@shared/slices/portfolio.slice.ts";
import CreateOrderModal from "features/portfolio/CreateOrder/CreateOrderModal.tsx";
import usePageWrapper from "common/Home/PageWrapper/usePageWrapper.ts";
import { portfolioFilters } from "@shared/slices/filters.slice.ts";
import { useBottomNavigator, useDisablePageScroll } from "utils/hooks/misc.tsx";
import FiltersChips from "common/Filters/FiltersChips/FiltersChips.tsx";
import PageTabs, { TabItem } from "common/Tabs/PageTabs.tsx";
import Accounts from "features/portfolio/Accounts/Accounts.tsx";
import PortfolioProvider from "features/portfolio/PortfolioProvider.tsx";
import PositionsProvider from "features/portfolio/Positions/PositionsProvider.tsx";
import { PositionsContext } from "features/portfolio/Positions/Positions.context.ts";
import useMedia from "utils/hooks/useMedia.ts";
import { useSelector } from "react-redux";
import { isContractsSymbol } from "@shared/utils/misc.ts";
import ExpandCollapse from "common/Animations/ExpandCollapse.tsx";
import FiltersButton from "common/Buttons/FiltersButton.tsx";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import AccountsPageProvider from "features/portfolio/Accounts/AccountsPageProvider.tsx";
import AccountsSortSelect from "features/portfolio/Accounts/AccountsSortSelect.tsx";
import { useRouteMatch } from "react-router-dom";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useIsSubUser, useSelectUser } from "@shared/utils/hooks/misc.ts";
import NewLookPortfolioIntro from "features/portfolio/NewLook/NewLookPortfolioIntro.tsx";
import NewLookLayoutSwitch from "features/portfolio/NewLook/NewLookLayoutSwitch.tsx";
import { NewLookPortfolioContext } from "features/portfolio/NewLook/NewLookPortfolio.context.ts";
import { NewLookToolbarGroup } from "features/portfolio/NewLook/styles.ts";
import NewLookFiltersBar from "features/portfolio/NewLook/NewLookFiltersBar.tsx";
import { PhoneChipsFade } from "features/dashboard/PhoneDashboard/PhoneSummary.tsx";

const PortfolioPage = () => {
  return (
    <PortfolioProvider>
      <PositionsProvider>
        <AccountsPageProvider>
          <PortfolioPageContent />
        </AccountsPageProvider>
      </PositionsProvider>
    </PortfolioProvider>
  );
};

const PortfolioPageContent = () => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  useDisablePageScroll(isBottomNavigator);
  const isSmallScreen = useMedia([`(max-width: 1000px)`], [true], false);
  const useFiltersChips = useMedia([`(max-width: 1400px)`], [true], false);
  const isFiltersSingleRowView = useMedia(
    [`(max-width: 550px)`],
    [true],
    false
  );
  const [showFilters, setShowFilters] = useState(false);
  const isSubUser = useIsSubUser();
  const { isNewLook } = useTheme();

  const tabToDisplayName = useMemo(
    () => ({
      [PortfolioTab.ACCOUNTS]: t("tabs.accounts"),
      [PortfolioTab.ASSETS]: t("tabs.assets"),
      [PortfolioTab.POSITIONS]: t("tabs.positions"),
    }),
    [i18n.language]
  );

  const { useMobileSearch } = useContext(PageWrapperContext)!;
  const {
    sortBy: positionsSortBy,
    setSortBy: positionsSetSortBy,
    sortDirection: positionsSortDirection,
    setSortDirection: positionsSetSortDirection,
  } = useContext(PositionsContext)!;
  const {
    isPositions,
    isCardsView,
    setIsCardsView,
    setSearchValue,
    buyModalOpen,
    setBuyModalOpen,
    loading,
    selectedTab,
    setSelectedTab,
    tabToNumItems,
  } = useContext(PortfolioContext)!;

  const user = useSelectUser();
  const isAccountsRoute = !!useRouteMatch(PORTFOLIO_ACCOUNTS_PATH);
  const isAssetsRoute = !!useRouteMatch(PORTFOLIO_ASSETS_PATH);
  const isAccounts = isBottomNavigator
    ? selectedTab === PortfolioTab.ACCOUNTS
    : isAccountsRoute;
  const isAssets = isBottomNavigator
    ? selectedTab === PortfolioTab.ASSETS
    : isAssetsRoute;

  const portfolioData = useSelector(selectConsolidatedPortfolioData);
  const isTwoRowsHeader = isPositions && isSmallScreen;

  const numOfPositions = useMemo(
    () => portfolioData.filter((coin) => isContractsSymbol(coin.symbol)).length,
    [portfolioData]
  );

  const hideFilterTypes = useMemo(
    () =>
      isAccounts
        ? [FilterType.AMOUNT_TYPE]
        : [FilterType.AMOUNT_TYPE, FilterType.STATUS],
    [isAccounts]
  );
  const { actions, selectors } = portfolioFilters;

  usePageWrapper({
    filters: {
      loadingStatesSelector: selectPortfolioDataLoadingStates,
      actions,
      selectors,
      hideFilterTypes,
    },
    showSearch: true,
    dependencies: [isAccounts],
    hideMobileFilters: true,
  });

  const { tab, tabDisplayName, onChange } = useTabsWithUrl<PortfolioTab>({
    tabToDisplayName,
    tabs: [PortfolioTab.ASSETS, PortfolioTab.POSITIONS, PortfolioTab.ACCOUNTS],
    path: PORTFOLIO_TABS_PATH,
    defaultTab: isAssets ? PortfolioTab.ASSETS : PortfolioTab.ACCOUNTS,
    keepQuery: false,
    skipUrlInitialization: isBottomNavigator,
  });

  const [searchInput] = useSearchInput({
    placeholder: tCommon("labels.search_placeholder"),
    style: {
      width: isPositions || !isSmallScreen ? 200 : 150,
      borderRadius: 8,
    },
    onChange: (value) => setSearchValue(value),
  });

  useEffect(() => {
    if (numOfPositions > CARDS_VIEW_MAX_POSITIONS) {
      setIsCardsView?.(false);
    }
  }, [numOfPositions]);

  const layoutChangeButtons = useMemo(
    () =>
      isPositions && numOfPositions <= CARDS_VIEW_MAX_POSITIONS ? (
        <LayoutSwitch
          layoutIcons={[<MenuOutlined />, <TableOutlined />]}
          selectedLayoutIndex={isCardsView ? 1 : 0}
          onClick={() => setIsCardsView?.(!isCardsView)}
          style={{ height: 32 }}
        />
      ) : undefined,
    [isPositions, isCardsView]
  );

  const positionsSortSelect = useMemo(
    () => (
      <SortSelect
        sortBy={positionsSortBy}
        setSortBy={positionsSetSortBy}
        sortDirection={positionsSortDirection}
        setSortDirection={positionsSetSortDirection}
        sortValues={Object.values(PositionsSort)}
        rounded={true}
        isSingleFiltersChipsRow={isBottomNavigator}
        labelMapper={(value) => t(value)}
      />
    ),
    [positionsSortBy, positionsSortDirection, i18n.language]
  );

  const getSortSelect = () => {
    if (!isBottomNavigator) return;
    if (isPositions) {
      return positionsSortSelect;
    } else if (isAccounts) {
      return <AccountsSortSelect />;
    }
  };

  const filtersChips = isNewLook ? (
    <NewLookFiltersBar
      filtersActions={actions}
      filtersSelectors={selectors}
      hideFilterTypes={hideFilterTypes}
      style={FILTERS_STYLE}
      extraComponent={getSortSelect()}
    />
  ) : (
    <FiltersChips
      filtersActions={actions}
      filtersSelectors={selectors}
      hideFilterTypes={hideFilterTypes}
      style={FILTERS_STYLE}
      extraComponent={getSortSelect()}
    />
  );

  const buyButton = (
    <Button
      type="primary"
      style={{ borderRadius: 6 }}
      loading={loading}
      disabled={loading}
      onClick={() => setBuyModalOpen(true)}
    >
      <PlusOutlined />
      {t("buttons.buy")}
    </Button>
  );

  const tabsItems = useMemo(() => {
    const items = [
      {
        key: tabToDisplayName[PortfolioTab.ASSETS],
        label: tabToDisplayName[PortfolioTab.ASSETS],
        link: PORTFOLIO_ASSETS_PATH,
        tab: PortfolioTab.ASSETS,
      },
      {
        key: tabToDisplayName[PortfolioTab.POSITIONS],
        label: tabToDisplayName[PortfolioTab.POSITIONS],
        link: PORTFOLIO_POSITIONS_PATH,
        tab: PortfolioTab.POSITIONS,
      },
    ];
    return items.map((tabItem) => {
      const numItems = tabToNumItems[tabItem.tab];
      return {
        ...tabItem,
        label: `${tabItem.label} ${loading ? "" : `(${numItems || 0})`}`,
      };
    });
  }, [tabToNumItems, tabToDisplayName, loading]);

  const onTabChange = useCallback((tab: TabItem) => {
    setSelectedTab(tab.key as PortfolioTab);
  }, []);

  const getExpandableChipsContent = () => {
    return (
      <ExpandCollapse expanded={showFilters || isFiltersSingleRowView}>
        {filtersChips}
      </ExpandCollapse>
    );
  };

  const filtersButton = useFiltersChips && !isFiltersSingleRowView && (
    <FiltersButton
      onClick={() => setShowFilters(!showFilters)}
      selectors={selectors}
    />
  );

  const mobileTabsItems: TabItem[] = useMemo(
    () => [
      {
        key: PortfolioTab.ACCOUNTS,
        label: tabToDisplayName[PortfolioTab.ACCOUNTS],
        children: TAB_TO_COMPONENT[PortfolioTab.ACCOUNTS],
        link: PORTFOLIO_ACCOUNTS_PATH,
        forceRender: true,
      },
      {
        key: PortfolioTab.ASSETS,
        label: tabToDisplayName[PortfolioTab.ASSETS],
        children: TAB_TO_COMPONENT[PortfolioTab.ASSETS],
        link: PORTFOLIO_ASSETS_PATH,
        forceRender: true,
      },
      {
        key: PortfolioTab.POSITIONS,
        label: tabToDisplayName[PortfolioTab.POSITIONS],
        children: TAB_TO_COMPONENT[PortfolioTab.POSITIONS],
        link: PORTFOLIO_POSITIONS_PATH,
        forceRender: true,
      },
    ],
    [tabToDisplayName]
  );

  // Stable, so the page toolbar that shows it isn't re-set on every render.
  const newLookFiltersBar = useMemo(
    () =>
      isNewLook ? (
        <NewLookFiltersBar
          filtersActions={actions}
          filtersSelectors={selectors}
          hideFilterTypes={hideFilterTypes}
        />
      ) : undefined,
    [isNewLook, hideFilterTypes, actions, selectors]
  );

  const getNewLookContent = () => {
    const canShowLayoutSwitch =
      isPositions && numOfPositions <= CARDS_VIEW_MAX_POSITIONS;
    const listActions = (
      <NewLookToolbarGroup>
        {!useMobileSearch && searchInput}
        {isPositions && positionsSortSelect}
        {canShowLayoutSwitch && (
          <NewLookLayoutSwitch
            isCardsView={!!isCardsView}
            onClick={() => setIsCardsView?.(!isCardsView)}
          />
        )}
      </NewLookToolbarGroup>
    );
    return (
      <>
        <NewLookPortfolioIntro
          activeTab={tabDisplayName}
          tabsItems={tabsItems}
          onTabChange={onChange}
          showBuy={!isSubUser && !user?.isWhopExperience()}
          loading={loading}
          onBuyClick={() => setBuyModalOpen(true)}
          filters={newLookFiltersBar}
        />
        <NewLookPortfolioContext.Provider value={{ listActions }}>
          {TAB_TO_COMPONENT[tab || PortfolioTab.ASSETS]}
        </NewLookPortfolioContext.Provider>
      </>
    );
  };

  const getContent = () => {
    if (isNewLook && !isBottomNavigator && !isAccounts) {
      return getNewLookContent();
    }
    if (isBottomNavigator) {
      return (
        <PageTabs
          items={mobileTabsItems}
          commonContent={
            isNewLook ? (
              <PhoneChipsFade>{filtersChips}</PhoneChipsFade>
            ) : (
              filtersChips
            )
          }
          onTabChange={onTabChange}
          isInstant={true}
        />
      );
    } else if (isAccounts) {
      return TAB_TO_COMPONENT[PortfolioTab.ACCOUNTS];
    }
    return (
      <>
        <StickyHeader style={STICKY_HEADER_STYLE}>
          <HeaderInnerWrapper $isTwoRowsHeader={isTwoRowsHeader}>
            <StyledTabs
              activeKey={tabDisplayName}
              onChange={onChange}
              items={tabsItems}
            />
            <ActionsWrapper $isTwoRowsHeader={isTwoRowsHeader}>
              {!useMobileSearch && searchInput}
              {isPositions && positionsSortSelect}
              {layoutChangeButtons}
              {filtersButton}
              {!isSubUser && !user?.isWhopExperience() && buyButton}
            </ActionsWrapper>
          </HeaderInnerWrapper>
          {useFiltersChips && getExpandableChipsContent()}
        </StickyHeader>
        {TAB_TO_COMPONENT[tab || PortfolioTab.ASSETS]}
      </>
    );
  };

  return (
    <>
      {getContent()}
      <CreateOrderModal
        closeModal={() => setBuyModalOpen(false)}
        isOpen={buyModalOpen}
      />
    </>
  );
};

export default React.memo(PortfolioPage);

const STICKY_HEADER_STYLE = { padding: "0 16px" };
const FILTERS_STYLE = { padding: "0 0 12px" };

const CARDS_VIEW_MAX_POSITIONS = 100;

const TAB_TO_COMPONENT = {
  [PortfolioTab.ACCOUNTS]: <Accounts />,
  [PortfolioTab.ASSETS]: <Assets />,
  [PortfolioTab.POSITIONS]: <Positions />,
};

const HeaderInnerWrapper = styled(Flex)<{ $isTwoRowsHeader?: boolean }>`
  flex-direction: ${(p) => (p.$isTwoRowsHeader ? "column" : "row")};
  align-items: ${(p) => (p.$isTwoRowsHeader ? "flex-start" : "flex-end")};
  padding: 12px 0;
`;
const StyledTabs = styled(Tabs)`
  &.ant-tabs-top > .ant-tabs-nav {
    margin: 0;
  }
  &.ant-tabs .ant-tabs-tab {
    padding-top: 0;
  }
`;
const ActionsWrapper = styled(Flex)<{ $isTwoRowsHeader?: boolean }>`
  column-gap: 8px;
  width: 100%;
  justify-content: flex-end;

  ${(p) =>
    p.$isTwoRowsHeader &&
    `
    padding-top: 12px;
  `};
`;
